import { TicketTrigger, TicketType } from "@/constants/tickets.ts";
import {
  AppContext,
  type TicketTriggerToFullDetailsComponentMap,
  type TicketTypeToFullDetailsComponentMap,
} from "@/types.ts";
import AnomalyDetectionFullDetails from "../AnomalyDetectionFullDetails.vue";
import DevicePasswordMissingFullDetails from "../DevicePasswordMissingFullDetails.vue";
import EmailTicketsFullDetails from "../EmailTicketFullDetails.vue";
import EdrAffectedDevicesFullDetails from "../EdrAffectedDevicesFullDetails.vue";
import FileIntegrityViolationFullDetails from "../FileIntegrityViolationFullDetails.vue";
import FindingsFullDetails from "../FindingsFullDetails.vue";
import FileFindingsFullDetails from "../FileFindingsFullDetails.vue";
import MalwareInCloudDriveFullDetails from "../MalwareInCloudDriveFullDetails.vue";
import MassDeleteDownloadFullDetails from "../MassDeleteDownloadFullDetails.vue";
import ProxyMisconfigurationFullDetails from "../ProxyMisconfigurationFullDetails.vue";
import UnifiedFindingsFullDetails from "../UnifiedFindingsFullDetails.vue";
import MspFileFindingsFullDetails from "@/components/msp/tickets/MspFileFindingsFullDetails.vue";
import MspEdrAffectedDevicesFullDetails from "@/components/msp/tickets/MspEdrAffectedDevicesFullDetails.vue";
import MspMassDeleteDownloadFullDetails from "@/components/msp/tickets/MspMassDeleteDownloadFullDetails.vue";
import UnifiedDeviceDlpFindingsFullDetails from "@/components/tickets/ticket-preview-details/full-details/UnifiedDeviceDlpFindingsFullDetails.vue";
import ImpossibleTravelerFullDetails from "@/components/tickets/ticket-preview-details/full-details/ImpossibleTravelerFullDetails.vue";
import UnifiedEmailDlpFindingsFullDetails from "@/components/tickets/ticket-preview-details/full-details/UnifiedEmailDlpFindingsFullDetails.vue";

export interface TicketFullDetailsConfig {
  components: TicketTypeToFullDetailsComponentMap;
  triggerComponents: TicketTriggerToFullDetailsComponentMap;
  sensitiveTypes: TicketType[];
  dlpTriggers: TicketTrigger[];
  context: AppContext;
}

export type TicketFullDetailsConfigOverrides = {
  components?: Partial<TicketTypeToFullDetailsComponentMap>;
  triggerComponents?: Partial<TicketTriggerToFullDetailsComponentMap>;
  sensitiveTypes?: TicketType[];
  dlpTriggers?: TicketTrigger[];
  context?: AppContext;
};

export const defaultTicketFullDetailsConfig: TicketFullDetailsConfig = {
  context: AppContext.SINGLE_WORKSPACE,
  components: {
    [TicketType.MALWARE_IN_CLOUD_DRIVE]: MalwareInCloudDriveFullDetails,
    [TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL]: FindingsFullDetails,
    [TicketType.SUSPECTED_DATA_EXPOSURE_BY_SHARING]: FindingsFullDetails,
    [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL]: FindingsFullDetails,
    [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_SHARING]: FindingsFullDetails,
    [TicketType.PROMPT_INJECTION]: FindingsFullDetails,
    [TicketType.USER_INACTIVITY]: AnomalyDetectionFullDetails,
    [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE]: FileFindingsFullDetails,
    [TicketType.MALWARE_IN_EMAIL_ATTACHMENTS]: EmailTicketsFullDetails,
    [TicketType.EMAIL_PHISHING]: EmailTicketsFullDetails,
    [TicketType.ABNORMAL_ADMIN_ACTIVITY]: AnomalyDetectionFullDetails,
    [TicketType.SUSPECTED_BOT_ATTACKS]: AnomalyDetectionFullDetails,
    [TicketType.ACCESS_PERMISSIONS_VIOLATION]: AnomalyDetectionFullDetails,
    [TicketType.BLOCKED_IP_LOGIN]: AnomalyDetectionFullDetails,
    [TicketType.SUSPECTED_IDENTITY_COMPROMISE]: AnomalyDetectionFullDetails,
    [TicketType.IMPOSSIBLE_TRAVELER]: ImpossibleTravelerFullDetails,
    [TicketType.EDR_DETECTION]: EdrAffectedDevicesFullDetails,
    [TicketType.BLOCKED_CONNECTION]: EdrAffectedDevicesFullDetails,
    [TicketType.FILE_INTEGRITY_VIOLATION]: FileIntegrityViolationFullDetails,
    [TicketType.PROXY_MISCONFIGURATION]: ProxyMisconfigurationFullDetails,
    [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_EMAIL]:
      UnifiedEmailDlpFindingsFullDetails,
    // Defaults to UnifiedFindingsFullDetails to support legacy variant
    // otherwise overridden with UnifiedDeviceDlpFindingsFullDetails in TicketFullDetails.vue
    [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_SHARING]: UnifiedFindingsFullDetails,
    [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_DEVICE]:
      UnifiedDeviceDlpFindingsFullDetails,
  },
  triggerComponents: {
    [TicketTrigger.DEVICE_PASSWORD_MISSING]: DevicePasswordMissingFullDetails,
    [TicketTrigger.MASS_DELETE]: MassDeleteDownloadFullDetails,
    [TicketTrigger.MASS_DOWNLOAD]: MassDeleteDownloadFullDetails,
  },
  sensitiveTypes: [
    TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL,
    TicketType.SUSPECTED_DATA_EXPOSURE_BY_SHARING,
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL,
    TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_SHARING,
    TicketType.MALWARE_IN_EMAIL_ATTACHMENTS,
    TicketType.EMAIL_PHISHING,
  ],
  dlpTriggers: [
    TicketTrigger.CRITICAL_DATA_KEYWORDS,
    TicketTrigger.CRITICAL_DATA_CERTIFICATE,
    TicketTrigger.CRITICAL_DATA_SOURCE_CODE,
    TicketTrigger.CRITICAL_DATA_PASSWORD,
    TicketTrigger.CRITICAL_DATA_FILE_TYPES,
    TicketTrigger.DLP_PERSONAL,
    TicketTrigger.DLP_CREDIT_CARD,
    TicketTrigger.DLP_NON_PUBLIC,
    TicketTrigger.DLP_HEALTH,
    TicketTrigger.DLP_EMAIL_SENSITIVE_DATA,
    TicketTrigger.DLP_CLOUD_SENSITIVE_DATA,
  ],
};

export const mspTicketFullDetailsConfig = createTicketFullDetailsConfig({
  components: {
    [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE]: MspFileFindingsFullDetails,
    [TicketType.EDR_DETECTION]: MspEdrAffectedDevicesFullDetails,
    [TicketType.BLOCKED_CONNECTION]: MspEdrAffectedDevicesFullDetails,
  },
  triggerComponents: {
    [TicketTrigger.MASS_DELETE]: MspMassDeleteDownloadFullDetails,
    [TicketTrigger.MASS_DOWNLOAD]: MspMassDeleteDownloadFullDetails,
  },
  context: AppContext.MSP,
});

export function createTicketFullDetailsConfig(
  overrides: TicketFullDetailsConfigOverrides = {}
): TicketFullDetailsConfig {
  const components = {
    ...defaultTicketFullDetailsConfig.components,
    ...overrides.components,
  } as TicketTypeToFullDetailsComponentMap;

  const triggerComponents = {
    ...defaultTicketFullDetailsConfig.triggerComponents,
    ...overrides.triggerComponents,
  } as TicketTriggerToFullDetailsComponentMap;

  return {
    components,
    triggerComponents,
    sensitiveTypes: overrides.sensitiveTypes ?? defaultTicketFullDetailsConfig.sensitiveTypes,
    dlpTriggers: overrides.dlpTriggers ?? defaultTicketFullDetailsConfig.dlpTriggers,
    context: overrides.context ?? defaultTicketFullDetailsConfig.context,
  };
}
